/**
 * Build-time toggle source: the Drupal "Feature Flags" taxonomy (vocabulary
 * `feature_flags`, SRWEB-2093).
 *
 * The contract with Drupal:
 * - A term's name is a toggle name, spelled exactly as in `FeatureToggleName`
 *   (e.g. `MegaMenu`).
 * - A published term turns its toggle on. Unpublishing or deleting the term
 *   hands the toggle back to the env config; Drupal cannot force a toggle off.
 * - A term naming a `Config_` toggle, or no declared toggle, is ignored, and
 *   the build log lists it.
 *
 * Each environment builds from its own Drupal, so a term only affects the
 * environment whose Drupal holds it, from that environment's next build. The
 * provider reads the terms through a static query (`useDrupalFeatureToggles`)
 * and `gatsby-node.ts` reads them for page gating.
 *
 * Framework-free so `gatsby-node.ts` can import it.
 */
import { FeatureToggleName, IFeatureToggle } from './featureToggles.types';
import { isConfigToggle } from './featureToggles.utils';

/** The fields of a Feature Flags term that the toggle source reads. */
export interface IDrupalFeatureFlagTerm {
    readonly name?: string | null;
    /** Drupal's Published flag. */
    readonly status?: boolean | null;
}

export interface IDrupalFeatureToggles {
    /** Toggles turned on by a published term. */
    readonly toggles: IFeatureToggle[];
    /** Names of published terms that match no toggle Drupal may turn on. */
    readonly ignoredNames: string[];
}

const knownNames = new Set<string>(Object.values(FeatureToggleName));

/** Turn the Feature Flags terms into toggle values. Unpublished terms are skipped. */
export const parseDrupalFeatureFlagTerms = (
    terms?: ReadonlyArray<IDrupalFeatureFlagTerm | null> | null,
): IDrupalFeatureToggles => {
    const toggles: IFeatureToggle[] = [];
    const ignoredNames: string[] = [];

    (terms ?? []).forEach((term) => {
        const name = term?.name?.trim();
        if (!name || term?.status !== true) return;

        if (knownNames.has(name) && !isConfigToggle(name as FeatureToggleName)) {
            toggles.push({ name: name as FeatureToggleName, isEnabled: true });
        } else {
            ignoredNames.push(name);
        }
    });

    return { toggles, ignoredNames };
};
